/**
 * @file FullscreenService.ts
 * @role The single owner of the browser Fullscreen API: feature detection, entering and
 *       leaving fullscreen, and reporting state changes that originate outside the app.
 * @owns FullscreenService, BrowserFullscreenService, and the `fullscreenchange` listener.
 * @reads An injectable Document seam (defaults to the real document; absent in non-browser envs).
 * @neverDoes Render. Touch Pixi. Decide boot flow. Throw on a refused request or exit.
 * @invariants 13 (constructed by Bootstrapper, never a module singleton).
 * @see src/app/FullscreenConsent.ts, src/scenes/SettingsScene/SettingsPanel.ts
 */

/** The slice of Document we need; lets tests inject a fake and survives non-browser envs. */
export interface FullscreenDocument {
  fullscreenElement: Element | null;
  documentElement: { requestFullscreen?: (() => Promise<void>) | undefined };
  exitFullscreen?: (() => Promise<void>) | undefined;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface FullscreenService {
  /** True when this browser exposes the element Fullscreen API. iPhone Safari does not. */
  readonly supported: boolean;
  /** True while the page is currently displayed fullscreen. */
  readonly active: boolean;
  /**
   * Resolves true when granted, false when unsupported or refused. Never throws.
   *
   * MUST be called synchronously from inside a user gesture. Awaiting anything beforehand
   * consumes the browser's user-activation grant and the request is rejected.
   */
  request(): Promise<boolean>;
  /** Leave fullscreen. No-op when not fullscreen. Never throws. */
  exit(): Promise<void>;
  /**
   * Observe fullscreen state, including changes the app did not initiate — pressing Escape or
   * a system gesture leaves fullscreen without going through `exit()`, and any UI reflecting
   * the state has to follow. A direct callback rather than EventBus, because this drives
   * control flow; see the same reasoning on LayoutService.subscribe.
   */
  subscribe(listener: (active: boolean) => void): () => void;
  dispose(): void;
}

function defaultDocument(): FullscreenDocument | undefined {
  return typeof document === 'undefined' ? undefined : document;
}

export class BrowserFullscreenService implements FullscreenService {
  private readonly listeners = new Set<(active: boolean) => void>();
  private readonly onChange = (): void => {
    const active = this.active;
    for (const listener of [...this.listeners]) listener(active);
  };

  constructor(private readonly doc: FullscreenDocument | undefined = defaultDocument()) {
    this.doc?.addEventListener('fullscreenchange', this.onChange);
  }

  get supported(): boolean {
    return typeof this.doc?.documentElement.requestFullscreen === 'function';
  }

  get active(): boolean {
    return this.doc?.fullscreenElement != null;
  }

  async request(): Promise<boolean> {
    const target = this.doc?.documentElement;
    if (typeof target?.requestFullscreen !== 'function') return false;
    try {
      // Invoked synchronously by design: awaiting anything first would consume the user
      // activation the browser requires, and the call would be rejected.
      await target.requestFullscreen();
      return true;
    } catch {
      // A refused request is a normal outcome, not an error. The game plays windowed.
      return false;
    }
  }

  async exit(): Promise<void> {
    const exitFullscreen = this.doc?.exitFullscreen;
    if (!this.active || typeof exitFullscreen !== 'function') return;
    try {
      await exitFullscreen.call(this.doc);
    } catch {
      // A refused exit is harmless; state stays as the browser reports it.
    }
  }

  subscribe(listener: (active: boolean) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    this.doc?.removeEventListener('fullscreenchange', this.onChange);
    this.listeners.clear();
  }
}
